import React, {useMemo} from "react";
import {EmployeeInReport} from "@/data/entities/employee";
import {dateFormatPattern, DayItem, HhMm, minutesToHhMm} from "@/utils/DateUtils";
import {Badge, Button} from "peggirkit";
import {editEmployee} from "@/routing/Router";
import dayjs from "dayjs";
import {getTimeDifference, TimeDiff} from "@widgets/employees/editor/timeEntries/columns/WorkedHoursSummaryFormatter";
import {FormattedMessage} from "react-intl";
import {twJoin} from "tailwind-merge";
import {colClassName, titleColClassName} from "@widgets/reports/timesheet/chart/TimesheetTable";
import TimeEntryInReport from "@widgets/reports/timesheet/chart/TimeEntryInReport";
import {SetStateFn} from "@/utils/SetStateUtils";


type Props = {
    employee: EmployeeInReport,
    daysBetween: DayItem[],
    setData: SetStateFn<EmployeeInReport[]>,
};

const EmployeeRow = ({employee, daysBetween, setData}: Props) => {
    const {timeEntries} = employee;
    const entriesByDay: Record<string, TimeDiff[]> = useMemo(
        () =>
            timeEntries.reduce<Record<string, TimeDiff[]>>(
                (acc, entry) => {
                    const day: string = dayjs(new Date(entry.clockIn)).format(dateFormatPattern).toLowerCase();
                    const diff = getTimeDifference(entry)
                    if (diff === null) return acc;

                    (acc[day] ??= []).push(diff);
                    return acc;
                },
                {}
            ),
        [timeEntries]);
    const {hours: totalHours, minutes: totalMinutes}: HhMm = useMemo(
        () => {
            let workedMinutes = 0;

            Object.values(entriesByDay).map(diffs => {
                diffs.map(d => {
                    workedMinutes += d.workedMinutes
                })
            })

            return minutesToHhMm(workedMinutes);
        },
        [entriesByDay]);

    return (
        <tr className="group">
            <td
                className={twJoin(
                    titleColClassName,
                    "font-medium",
                )}
            >
                <Button
                    variant={"link"}
                    buttonAction={{
                        as: "reactRouterLink",
                        actionProps: {
                            to: editEmployee(employee.id).absolutePath(),
                        },
                    }}
                >
                    {employee.firstName}{" "}{employee.lastName}
                </Button>
            </td>

            <td
                className={twJoin(
                    colClassName,
                    "border-b border-r",
                )}
            >
                <Badge color={"success"}>
                    <FormattedMessage
                        id={"formattedHoursMinutes"}
                        values={{hours: totalHours, minutes: totalMinutes}}
                    />
                </Badge>
            </td>

            {daysBetween.map((day) => {
                const entries: TimeDiff[] | undefined = entriesByDay[day.formattedDay]
                return (
                    <td
                        key={day.formattedDay}
                        className={twJoin(
                            colClassName,
                            "border-b",
                        )}
                    >
                        {
                            entries
                                ? <div className={"flex flex-row gap-2"}>
                                    {entries.map((e: TimeDiff, i: number) =>
                                        <TimeEntryInReport
                                            key={i}
                                            timeDiff={e}
                                            employee={employee}
                                            setData={setData}
                                        />
                                    )}
                                </div>
                                : <span className={"text-gray-300 dark:text-slate-500"}>-</span>
                        }
                    </td>
                );
            })}
        </tr>
    );
};

export default EmployeeRow;